/**
 * 計算式の入力欄（#3145 / #3170）。
 * 入力のたびに構文チェックし、エラーはその場に赤字表示する（エラーのままでは保存できない）。
 * 項目参照は「項目を挿入」プルダウン（項目名で表示）からフィールドコードを挿入する。
 */
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import CustomTextfield from '../form/CustomTextfield';
import {
  gray80,
  gray100,
  green50,
  red50,
  secondaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldXs, regularXs } from '../../../constants/location';
import {
  CALCULATION_FORMULA_HELP_TEXT,
  calculationFormulaExamples,
  calculationFunctionInsertOptions,
} from '../../../constants/options';
import { validateFormula } from '../../../utils/calculation';
import type { ValidateFormulaResult } from '../../../utils/calculation';
import { findChoiceLabelComparisons } from '../../../utils/pattern/formulaChoiceError';
import type { ItemChoiceType } from '../../../types/common';

/** 「項目を挿入」プルダウンに出す参照可能な項目（自分自身は含めない） */
export type FormulaReferenceItem = {
  /** 挿入されるフィールドコード */
  fieldCode: string;
  /** プルダウンに表示する項目名 */
  itemName: string;
  /** 選択肢項目の選択肢（#3403）。表示名で比較している式の検証に使う */
  choices?: ItemChoiceType[];
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** この式から参照できる項目（参照スコープ適用済み・自分自身は除く） */
  referenceableItems: FormulaReferenceItem[];
  /** SUM で集計できる明細の列（#3358）。単一項目の編集時のみ渡る */
  aggregatableItems?: FormulaReferenceItem[];
  /** パターン内の全フィールドコード（スコープ違反の判定用） */
  allFieldCodes: string[];
  /** 編集中の項目自身のフィールドコード（自己参照の検知用） */
  selfFieldCode?: string;
  /** 編集中の項目自身の選択肢（#3403）。選択肢型のとき、結果を表示名で書いていないかの検証に使う */
  selfChoices?: ItemChoiceType[];
  /** 検証結果（エラーの有無）が変わったときに通知する。保存ボタンの活性制御に使う */
  onValidationChange?: (isValid: boolean) => void;
  disabled?: boolean;
};

const styles = {
  insertButton: {
    ...secondaryButtonBackgroundColor,
    height: '36px',
  },
  helperText: {
    ...regularXs,
    color: gray80,
    lineHeight: '1.8',
  },
  errorText: {
    ...regularXs,
    color: red50,
    marginTop: '6px',
  },
  validText: {
    ...regularXs,
    color: green50,
    marginTop: '6px',
  },
  warningText: {
    ...regularXs,
    color: gray80,
    marginTop: '6px',
  },
  // 設定例の中で結果だけを目立たせる（式・前提の値は helperText のまま）
  exampleResult: {
    ...boldXs,
    color: gray100,
  },
};

/** 設定例の前提の値を「単価=1000、数量=3」の形にする */
const formatExampleValues = (values: Record<string, string>): string =>
  Object.entries(values)
    .map(([itemName, value]) => `${itemName}=${value}`)
    .join('、');

const FormulaEditor = ({
  value,
  onChange,
  referenceableItems,
  aggregatableItems = [],
  allFieldCodes,
  selfFieldCode,
  selfChoices,
  onValidationChange,
  disabled = false,
}: Props): ReactElement => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [itemMenuAnchor, setItemMenuAnchor] = useState<HTMLElement | null>(null);
  const [functionMenuAnchor, setFunctionMenuAnchor] = useState<HTMLElement | null>(null);

  // 入力のたびに静的検証する（構文・未知参照・スコープ違反・自己参照・正規表現）
  const validationResult: ValidateFormulaResult | null = useMemo(() => {
    if (value.trim() === '') return null;
    return validateFormula(value, {
      referenceableFieldCodes: referenceableItems.map(item => item.fieldCode),
      allFieldCodes,
      selfFieldCode,
    });
  }, [value, referenceableItems, allFieldCodes, selfFieldCode]);

  const isValid = validationResult?.isValid === true;

  // 選択肢の表示名を出力値のつもりで書いている箇所（#3403）。
  // 参照・保存とも出力値で扱われるため意図どおりに動かない。エラーとして保存できないようにする
  const choiceErrors: string[] = useMemo(
    () => (isValid ? findChoiceLabelComparisons(value, referenceableItems, selfChoices) : []),
    [isValid, value, referenceableItems, selfChoices],
  );
  const isSavable = isValid && choiceErrors.length === 0;

  useEffect(() => {
    onValidationChange?.(isSavable);
  }, [isSavable, onValidationChange]);

  /**
   * カーソル位置にテキストを挿入する。
   * cursorOffset は挿入後のカーソル位置（挿入テキスト先頭からのオフセット）
   */
  const insertAtCursor = (text: string, cursorOffset: number): void => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    const newValue = `${value.slice(0, start)}${text}${value.slice(end)}`;
    onChange(newValue);
    // 挿入後にカーソルを戻す（レンダリング反映後に実行）
    window.requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      const cursorPosition = start + cursorOffset;
      input.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleInsertItem = (fieldCode: string): void => {
    const text = `{${fieldCode}}`;
    insertAtCursor(text, text.length);
    setItemMenuAnchor(null);
  };

  /** 明細の列を SUM() で包んで挿入する（#3358）。カーソルは式の末尾に置く */
  const handleInsertAggregate = (text: string): void => {
    insertAtCursor(text, text.length);
    setItemMenuAnchor(null);
  };

  const handleInsertFunction = (insertText: string): void => {
    // 「LEFT()」の括弧の中にカーソルを置く
    insertAtCursor(insertText, insertText.length - 1);
    setFunctionMenuAnchor(null);
  };

  return (
    <Box>
      <CustomTextfield
        labelText="計算式"
        value={value}
        onChange={(e): void => onChange(e.target.value)}
        inputRef={inputRef}
        inputWidth="100%"
        placeholder="例）{単価} * {数量}"
        multiline
        rows={3}
        disabled={disabled}
        error={value.trim() !== '' && !isValid}
        formMb="0px"
      />

      <Box sx={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
        <Button
          sx={styles.insertButton}
          endIcon={<ArrowDropDownIcon />}
          onClick={(e): void => setItemMenuAnchor(e.currentTarget)}
          disabled={disabled || referenceableItems.length === 0}
        >
          項目を挿入
        </Button>
        <Menu
          anchorEl={itemMenuAnchor}
          open={Boolean(itemMenuAnchor)}
          onClose={(): void => setItemMenuAnchor(null)}
        >
          {referenceableItems.map(item => (
            <MenuItem key={item.fieldCode} onClick={(): void => handleInsertItem(item.fieldCode)}>
              {item.itemName}
            </MenuItem>
          ))}
          {/* 明細の列は SUM で合計する形で挿入する（#3358）。
              単一項目から行内参照はできないため、SUM() で包んで入れる */}
          {aggregatableItems.length > 0 && <Divider />}
          {aggregatableItems.map(item => (
            <MenuItem
              key={`sum-${item.fieldCode}`}
              onClick={(): void => handleInsertAggregate(`SUM({${item.fieldCode}})`)}
            >
              {`${item.itemName} の合計`}
            </MenuItem>
          ))}
        </Menu>

        <Button
          sx={styles.insertButton}
          endIcon={<ArrowDropDownIcon />}
          onClick={(e): void => setFunctionMenuAnchor(e.currentTarget)}
          disabled={disabled}
        >
          関数を挿入
        </Button>
        <Menu
          anchorEl={functionMenuAnchor}
          open={Boolean(functionMenuAnchor)}
          onClose={(): void => setFunctionMenuAnchor(null)}
        >
          {calculationFunctionInsertOptions.map(option => (
            <MenuItem
              key={option.value}
              onClick={(): void => handleInsertFunction(option.insertText)}
            >
              {option.option}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* {…} にはフィールドコードが入る旨のヘルプ（常時表示。設計書 §7.1） */}
      <Typography sx={{ ...styles.helperText, marginTop: '8px' }}>
        ※ {CALCULATION_FORMULA_HELP_TEXT}
      </Typography>

      {value.trim() === '' ? (
        <Typography sx={styles.errorText}>計算式を入力してください</Typography>
      ) : validationResult?.error ? (
        <Typography sx={styles.errorText}>{validationResult.error.message}</Typography>
      ) : choiceErrors.length > 0 ? (
        choiceErrors.map(error => (
          <Typography key={error} sx={styles.errorText}>
            {error}
          </Typography>
        ))
      ) : (
        <Typography sx={styles.validText}>✓ 式に問題はありません</Typography>
      )}
      {validationResult?.warnings.map(warning => (
        <Typography key={warning.code} sx={styles.warningText}>
          ⚠ {warning.message}
        </Typography>
      ))}

      <Box sx={{ marginTop: '8px' }}>
        {calculationFormulaExamples.map(example => (
          <Typography key={example.formula} sx={styles.helperText}>
            {`設定例）${example.formula} → `}
            <Box component="span" sx={styles.exampleResult}>
              {example.result}
            </Box>
            {`（${formatExampleValues(example.values)}）`}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default FormulaEditor;
